import React, { useState } from 'react';
import { Box, Typography, Card, CardContent, CircularProgress, Grid } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Activity, MapPinOff, AlertTriangle } from 'lucide-react';
import { fetchLocationActivitySummary } from '../../api/dashboardApi';
import LocationActivityDetailModal from './LocationActivityDetailModal';
import './FinanceDashboard.css';

const formatCurrency = (value) => {
  return `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const LocationActivityOverview = () => {
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState(6);
  const [selectedStatus, setSelectedStatus] = useState('inactive');

  const { data: summaryData, isLoading, isError } = useQuery({
    queryKey: ['locationActivitySummary'],
    queryFn: () => fetchLocationActivitySummary(),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div className="fd-finance-card" style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div className="fd-card-header">
            <h3 className="fd-card-title">Location Activity Highlights (≥ 6 Months)</h3>
          </div>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, color: 'red' }}>
            Failed to load location activity data.
          </Box>
        </div>
      </div>
    );
  }

  const breakdown = summaryData?.data?.breakdown || [];
  const asOfMonth = summaryData?.data?.as_of_month || 'N/A';

  // Only get the 6-month data
  const sixMonthData = breakdown.find(item => item.months_inactive_gte === 6) || {};

  const handleInactiveClick = () => {
    setSelectedMonths(6);
    setSelectedStatus('inactive');
    setDetailModalOpen(true);
  };

  const tiles = [
    {
      label: "Active Locations Ids",
      value: sixMonthData.active_count || 0,
      icon: <Activity size={22} strokeWidth={2.5} />,
      iconColor: "#10B981",
      bgColor: "#D1FAE5",
    },
    {
      label: "Idle Location IDs",
      value: sixMonthData.inactive_count || 0,
      icon: <MapPinOff size={22} strokeWidth={2.5} />,
      iconColor: "#EF4444",
      bgColor: "#FEE2E2",
      onClick: handleInactiveClick,
      isClickable: true
    }
  ];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} color="#1E293B">Location Activity Highlights (≥ 6 Months)</Typography>
        <Typography variant="body2" color="textSecondary" fontWeight="bold">
          As of {asOfMonth}
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ flex: 1, alignContent: 'flex-start' }}>
        {tiles.map((tile, index) => (
          <Grid item xs={12} sm={12} key={index}>
            <Card 
              onClick={tile.onClick}
              sx={{ 
                borderRadius: "10px", 
                boxShadow: "0 2px 10px rgba(0,0,0,0.03)", 
                border: tile.isClickable ? "1px solid #fca5a5" : "1px solid rgba(0,0,0,0.04)", 
                transition: "all 0.2s ease",
                cursor: tile.isClickable ? "pointer" : "default",
                "&:hover": { 
                  transform: tile.isClickable ? "translateY(-2px)" : "none", 
                  boxShadow: tile.isClickable ? "0 6px 16px rgba(239, 68, 68, 0.15)" : "0 6px 16px rgba(0,0,0,0.06)" 
                },
                height: "100%",
                bgcolor: tile.isClickable ? "#fef2f2" : "#ffffff"
              }}
            >
              <CardContent 
                sx={{ 
                  p: 2, 
                  "&:last-child": { pb: 2 },
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  <Box 
                    sx={{ 
                      p: 1, 
                      borderRadius: "8px", 
                      backgroundColor: tile.bgColor,
                      color: tile.iconColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {tile.icon}
                  </Box>
                  <Typography 
                    variant="h6" 
                    fontWeight={800} 
                    color={tile.isClickable ? "#b91c1c" : "#1E293B"} 
                    noWrap
                    sx={{ fontSize: "1.4rem", lineHeight: 1.2, letterSpacing: "-0.01em" }}
                  >
                    {tile.value}
                  </Typography>
                </Box>
                <Typography 
                  variant="subtitle2" 
                  color={tile.isClickable ? "#dc2626" : "#64748B"} 
                  fontWeight={600} 
                  sx={{ 
                    fontSize: "0.75rem", 
                    textTransform: "uppercase", 
                    letterSpacing: "0.02em",
                    lineHeight: 1.25,
                    textDecoration: tile.isClickable ? "underline" : "none"
                  }}
                >
                  {tile.isClickable ? `${tile.label} (Click to view)` : tile.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <LocationActivityDetailModal 
        open={detailModalOpen} 
        onClose={() => setDetailModalOpen(false)} 
        months={selectedMonths} 
        status={selectedStatus}
      />
    </Box>
  );
};

export default LocationActivityOverview;
